import { memo, useCallback, useMemo } from 'react';

import { EDIT_SCOPE } from '../../constants/editScope';
import { useEditorSession } from '../../hooks/useEditorSession';
import { usePermissionDraft } from '../../hooks/usePermissionDraft';
import { getFirstName } from '../../utils/memberDisplay';
import PermissionCounter from '../shared/PermissionCounter';
import { PermissionTree } from '../shared/PermissionTree';
import ScopeBanner from '../shared/ScopeBanner';
import UnsavedChangesModal from '../shared/UnsavedChangesModal';
import StudioEditFooter from './StudioEditFooter';

/**
 * Edit mode of a studio, scoped to ONE member: scope banner, counter, the editable permission
 * tree and the Save changes / Cancel footer. `onSave` resolves `true` when saved (the editor
 * then closes); on failure the edits are kept. Cancel with unsaved edits asks first.
 *
 * @param {object} props
 * @param {object} props.studio UI studio (see `useMemberStudios`)
 * @param {string} props.memberName
 * @param {(studioId: string, grants: object[]) => Promise<boolean>} props.onSave
 * @param {() => void} props.onClose Leaves edit mode (after a save, or on Cancel).
 * @param {(studioId: string) => void} props.onRemoveAccess
 */
const StudioRowEditor = ({ studio, memberName, onSave, onClose, onRemoveAccess }) => {
  const { draft, tags, isDirty, summary, grants, toggle, changeLevel } = usePermissionDraft(
    studio.tree,
    studio.grants
  );

  const roles = useMemo(
    () => studio.sources.filter((s) => s.type === 'role').map((s) => s.label),
    [studio.sources]
  );

  const saveGrants = useCallback(() => onSave(studio.id, grants), [onSave, studio.id, grants]);
  const { isSaving, isConfirmingDiscard, save, cancel, keepEditing, discard } = useEditorSession({
    isDirty,
    onSave: saveGrants,
    onClose,
  });

  const handleRemove = useCallback(() => onRemoveAccess(studio.id), [onRemoveAccess, studio.id]);

  return (
    <div className="flex flex-col gap-3">
      <ScopeBanner
        scope={EDIT_SCOPE.MEMBER}
        name={memberName}
        roles={roles}
        otherMembers={studio.otherMemberCount}
      />
      <PermissionCounter
        added={summary.direct}
        fromRoles={summary.fromRoles}
        name={getFirstName(memberName)}
      />
      <PermissionTree
        nodes={studio.tree}
        draft={draft}
        mode="edit"
        tags={tags}
        expand="top"
        onToggle={toggle}
        onLevelChange={changeLevel}
      />
      <StudioEditFooter
        canSave={isDirty}
        isSaving={isSaving}
        hasRoleAccess={studio.hasRoleAccess}
        memberName={getFirstName(memberName)}
        roleNames={roles}
        isDirectOnly={studio.isDirectOnly}
        onSave={save}
        onCancel={cancel}
        onRemoveAccess={handleRemove}
      />
      <UnsavedChangesModal
        isOpen={isConfirmingDiscard}
        onKeepEditing={keepEditing}
        onDiscard={discard}
      />
    </div>
  );
};

export default memo(StudioRowEditor);
